import { useEffect, useMemo, useState } from "react";
import { HitSectionLoading, useLoadingLevel } from "@/components/loading";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, publicImageUrl, cardImageUrl, cardName } from "@/lib/api";
import { useLightbox } from "@/lib/lightbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Star, MapPin, Package, CheckCircle2, Share2, Camera } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { cardPath } from "@/lib/cardUrl";
import { GameIcon } from "@/components/GameIcon";
import { useActiveGames, GAMES, inferGameOfCard } from "@/lib/game";
import ShareCatalogModal from "@/components/ShareCatalogModal";
import { useSellerFilters, SellerFiltersBar } from "@/components/SellerFilters";
import { HitAvatar, avatarBus } from "@/components/avatar/HitAvatar";
import { RankName, frameOf } from "@/components/avatar/RankPanel";
import { useAuth } from "@/lib/auth";

export default function SellerProfile() {
  // Soporta dos rutas: /vendedor/:sellerId (link viejo) y /tienda/:slug (link nuevo).
  const { sellerId, slug } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pageLevel = useLoadingLevel(loading);
  // Modal de compartir. Se puede pre-abrir con `?share=1` (útil para QA y
  // deep-links). Lo leemos una sola vez en el estado inicial para no
  // reabrirlo si el modal se cierra y el user queda con el query en la URL.
  const [shareOpen, setShareOpen] = useState(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("share") === "1"
  );
  const { open: openLightbox } = useLightbox();

  const [errorMsg, setErrorMsg] = useState(null);
  // Pestaña activa: "all" | "pokemon" | "riftbound". Empieza en "all" — el
  // vendedor puede haber compartido su catálogo mixto y queremos que quien
  // llega por primera vez vea todas sus cartas de un vistazo, y luego filtre.
  const [activeGame, setActiveGame] = useState("all");
  const ACTIVE_GAMES = useActiveGames();

  const load = () => {
    setLoading(true);
    setErrorMsg(null);
    // Backend endpoint distinto según la ruta. by-slug puede devolver
    // `{redirect: true, current_slug}` si el slug es histórico.
    const req = slug
      ? api.get(`/sellers/by-slug/${slug}`)
      : api.get(`/sellers/${sellerId}/profile`);
    req
      .then((r) => {
        if (r.data?.redirect && r.data?.current_slug) {
          // Slug histórico → 301 SPA al slug actual (reemplaza history).
          navigate(`/tienda/${r.data.current_slug}`, { replace: true });
          return;
        }
        setData(r.data);
        // Retrocompat: si venimos por /vendedor/:UUID y el perfil tiene slug,
        // reemplazamos la URL por la canonical /tienda/:slug (mejor SEO,
        // QRs viejos siguen funcionando por el redirect anterior).
        if (sellerId && r.data?.seller?.slug) {
          navigate(`/tienda/${r.data.seller.slug}`, { replace: true });
        }
      })
      .catch((err) => {
        setData(null);
        const status = err?.response?.status;
        if (status === 404) setErrorMsg("Este vendedor no está disponible o su verificación aún no fue aprobada.");
        else setErrorMsg(err?.response?.data?.detail || err?.message || "No se pudo cargar el catálogo del vendedor. Revisa tu conexión e intenta de nuevo.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [sellerId, slug]);

  const publications = data?.publications || [];
  const counts_by_game = data?.counts_by_game;

  // Segmentación por juego — usamos el `game` que envía el backend (que
  // aplica la regla dura de formato del ID), y como fallback lo inferimos
  // en cliente igual que en el catálogo principal. Nunca se mezclan cartas
  // de dos juegos en la vista filtrada.
  const filteredPublications = useMemo(() => {
    if (activeGame === "all") return publications;
    return publications.filter((p) => {
      const g = p.game || inferGameOfCard({ id: p.card_id });
      return g === activeGame;
    });
  }, [publications, activeGame]);
  const filterCtl = useSellerFilters(filteredPublications);
  const shownPublications = filterCtl.result;

  if (loading || pageLevel !== "none") return <div className="max-w-6xl mx-auto px-5 py-8 min-h-[70vh]"><HitSectionLoading level={pageLevel} loading={loading} section="profile" grid /></div>;
  if (!data) return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="seller-not-found">
      <Link to="/catalog" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-4 text-sm">
        <ArrowLeft size={16} /> Volver al catálogo
      </Link>
      <div className="bg-red-500/10 border border-red-500/40 rounded-md p-6 text-center">
        <div className="text-red-300 font-medium mb-2">Catálogo no disponible</div>
        <div className="text-sm text-red-200/80 mb-4">{errorMsg || "No se pudo cargar el catálogo de este vendedor."}</div>
        <Button type="button" onClick={load} className="bg-red-600 hover:bg-red-700" data-testid="seller-load-retry">
          Reintentar
        </Button>
      </div>
    </div>
  );

  const { seller, rating, sales_count } = data;

  const gameCount = (g) => {
    if (counts_by_game && typeof counts_by_game[g] === "number") return counts_by_game[g];
    // Fallback si el backend viejo aún no envía counts_by_game.
    return publications.filter((p) => (p.game || inferGameOfCard({ id: p.card_id })) === g).length;
  };
  const totalCount = publications.length;

  const shareCatalog = () => setShareOpen(true);
  const memberSince = seller.created_at
    ? new Date(seller.created_at).toLocaleDateString("es-EC", { month: "long", year: "numeric" })
    : "";

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Seo
        title={`Catálogo de ${seller.name} — Cartas Pokémon y Riftbound`}
        description={`Explora las ${publications.length} publicaciones activas de ${seller.name} en HitTCG. Vendedor verificado en Ecuador. Rating ${rating?.avg ? rating.avg.toFixed(1) + "★" : "sin calificar aún"}.`}
        url={seller.slug ? `/tienda/${seller.slug}` : `/vendedor/${sellerId || seller.id}`}
      />
      <Link to="/catalog" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-4 text-sm">
        <ArrowLeft size={16} /> Volver al catálogo
      </Link>

      <div className="bg-[#111] border border-[#262626] rounded-md p-4 sm:p-6 mb-6" data-testid="seller-profile-header">
        <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
          <div className="flex flex-col items-center gap-1.5 shrink-0"><HitAvatar avatarId={seller.avatar_id} name={seller.name} size={120} rank={frameOf(seller)} testid="seller-profile-avatar" /><RankName rank={seller.rank || "common"} /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl break-anywhere min-w-0 flex-1" data-testid="seller-profile-name">{seller.name}</h1>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 whitespace-nowrap">
                <CheckCircle2 size={12} className="mr-1" />Verificado
              </Badge>
            </div>
            {me?.id && me.id === seller.id && (
              <button type="button" onClick={() => avatarBus.openPicker("cambiar")} data-testid="seller-profile-change-avatar" className="mt-2 mr-2 h-9 px-4 rounded-full border border-white/15 text-sm font-semibold text-white/85 hover:border-[#FF4438] hover:text-white transition-colors">Cambiar avatar</button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={shareCatalog}
              data-testid="seller-share-btn"
              className="mt-3 border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10 w-full sm:w-auto"
            >
              <Share2 size={14} className="mr-1" /> Compartir catálogo
            </Button>
            <div className="text-sm text-neutral-400 mt-3 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><MapPin size={12} />{seller.city || "Ecuador"}</span>
              {memberSince && <span>Miembro desde {memberSince}</span>}
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-6 mt-4 text-sm">
              <div className="min-w-0">
                <div className="text-neutral-500 text-[10px] sm:text-xs uppercase leading-tight">Publicaciones activas</div>
                <div className="font-display text-xl sm:text-2xl mt-1" data-testid="seller-active-count">{publications.length}</div>
              </div>
              <div className="min-w-0">
                <div className="text-neutral-500 text-[10px] sm:text-xs uppercase leading-tight">Ventas cerradas</div>
                <div className="font-display text-xl sm:text-2xl mt-1">{sales_count}</div>
              </div>
              <div className="min-w-0">
                <div className="text-neutral-500 text-[10px] sm:text-xs uppercase leading-tight">Calificación</div>
                <div className="font-display text-xl sm:text-2xl mt-1 flex items-center gap-1 flex-wrap">
                  {rating.avg !== null ? (
                    <>
                      <Star size={18} className="fill-[#EF4444] text-[#EF4444] shrink-0" />
                      <span>{rating.avg}</span>
                      <span className="text-neutral-500 text-xs sm:text-sm">({rating.count})</span>
                    </>
                  ) : (
                    <span className="text-neutral-500 text-xs sm:text-sm">Sin calificaciones aún</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="font-display text-xl sm:text-2xl mb-4 flex items-center gap-2">
        <Package size={20} />Cartas de este vendedor
      </h2>

      {/* Pestañas por juego — mismo patrón que el catálogo principal para
          que jamás se mezclen Pokémon con Riftbound en la vista de un
          vendedor. "Todos" muestra la vista unificada por si el visitante
          quiere ver el catálogo completo del vendedor. */}
      {publications.length > 0 && (
        <div className="flex items-center gap-1 mb-4 border-b border-[#262626] overflow-x-auto" data-testid="seller-game-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeGame === "all"}
            data-testid="seller-game-tab-all"
            onClick={() => setActiveGame("all")}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${activeGame === "all" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-white"}`}
          >
            <span>Todos</span>
            <span className="text-[10px] text-neutral-500 bg-[#0A0A0A] border border-[#262626] rounded px-1.5 py-0.5">{totalCount}</span>
          </button>
          {ACTIVE_GAMES.map((g) => {
            const n = gameCount(g.key);
            return (
              <button
                key={g.key}
                type="button"
                role="tab"
                aria-selected={activeGame === g.key}
                data-testid={`seller-game-tab-${g.key}`}
                onClick={() => setActiveGame(g.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${activeGame === g.key ? "text-white" : "border-transparent text-neutral-500 hover:text-white"}`}
                style={activeGame === g.key ? { borderColor: g.color, color: "#fff" } : undefined}
              >
                <GameIcon game={g.key} size="sm" />
                <span>{g.label}</span>
                <span className="text-[10px] text-neutral-500 bg-[#0A0A0A] border border-[#262626] rounded px-1.5 py-0.5">{n}</span>
              </button>
            );
          })}
        </div>
      )}

      {publications.length > 0 && filteredPublications.length > 0 && <SellerFiltersBar ctl={filterCtl} total={filteredPublications.length} />}

      {publications.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500" data-testid="seller-empty">
          Este vendedor todavía no tiene cartas publicadas. Vuelve pronto o <Link to="/catalog" className="text-[#EF4444] hover:underline">explora el catálogo nacional</Link>.
        </div>
      ) : filteredPublications.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500" data-testid="seller-empty-game">
          {seller.display_name || seller.name} todavía no tiene cartas de <b className="text-neutral-300">{GAMES[activeGame]?.label}</b> publicadas. Prueba con otra pestaña.
        </div>
      ) : shownPublications.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500" data-testid="seller-empty-filters">
          Ninguna publicación coincide con tu búsqueda o filtros.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="seller-publications-grid">
          {shownPublications.map((p) => {
            const photoCount = p.photos?.length || 0;
            // Precio robusto: buyer_pays si existe, si no price, si no 0.
            // Antes rompía toda la página con "Cannot read properties of null
            // (reading 'toFixed')" cuando venía una publicación sin precio
            // computado → pantalla negra al abrir el link compartido.
            const rawPrice = p.buyer_pays ?? p.price ?? 0;
            const price = typeof rawPrice === "number" && Number.isFinite(rawPrice) ? rawPrice : 0;
            const lang = (p.language || "es").toUpperCase();
            const cond = p.condition && p.condition !== "na" ? p.condition : "—";
            return (
              <div
                key={p.id}
                data-testid={`seller-pub-${p.id}`}
                className="bg-[#111] border border-[#262626] rounded-md overflow-hidden hover:border-[#EF4444] transition-colors flex flex-col"
              >
                <Link
                  to={cardPath({ cardId: p.card_id, name: cardName(p.card, p.language), language: p.language, variant: p.variant })}
                  state={{ from: seller.slug ? `/tienda/${seller.slug}` : `/vendedor/${sellerId || seller.id}`, fromLabel: seller?.name || "el vendedor" }}
                  className="block relative"
                >
                  <div className="aspect-[3/4] bg-[#0A0A0A]">
                    {p.card?.image_url ? (
                      <img src={cardImageUrl(p.card.image_url)} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} />
                    ) : p.photos?.[0] ? (
                      <img src={publicImageUrl(p.photos[0])} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-xs text-neutral-600">Sin imagen</div>
                    )}
                  </div>
                  {photoCount > 0 && (
                    <div
                      className="absolute top-2 left-2 bg-black/80 border border-[#262626] rounded-full px-2 py-0.5 text-[10px] text-white flex items-center gap-1"
                      title="Fotos reales del vendedor"
                    >
                      <Camera size={10} /> {photoCount}
                    </div>
                  )}
                </Link>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="text-sm line-clamp-1">{cardName(p.card, p.language)}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="font-display text-lg text-[#EF4444]">${price.toFixed(2)}</span>
                    <span className="text-[10px] text-neutral-500">{lang} · {cond}</span>
                  </div>
                  {photoCount > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openLightbox(p.photos.map(ph => ({ path: ph, scope: "public" })), 0);
                      }}
                      data-testid={`seller-pub-photos-${p.id}`}
                      className="mt-2 text-[11px] text-[#EF4444] hover:underline text-left flex items-center gap-1"
                    >
                      <Camera size={12} /> Ver {photoCount} foto{photoCount === 1 ? "" : "s"} real{photoCount === 1 ? "" : "es"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ShareCatalogModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sellerName={seller.display_name || seller.name}
        slug={seller.slug}
        fallbackUrl={sellerId ? `/vendedor/${sellerId}` : null}
      />
    </div>
  );
}
